import { yupResolver } from "@hookform/resolvers/yup";
import LoadingFormNormal from "components/shared-components/LoadingFormNormal";
import MessageFormValidate from "components/shared-components/MessageFormValidate";
import { english } from "flatpickr/dist/l10n/default";
import { Vietnamese } from "flatpickr/dist/l10n/vn";
import { useDetailReport, useOrderStatus } from "hooks";
import { useHubList } from "hooks/hub-management/useHub";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import Flatpickr from "react-flatpickr";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { useToasts } from "react-toast-notifications";
import {
  Col,
  FormGroup,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import * as yup from "yup";
import { initOptionDateLocale, reportModeList, KPI_STATUS_OPTIONS, REPORT_TYPE_KPI_PERFORMANCE, buildReportYearOptions } from "./utils";
import { ORDER_TYPE_FILTER_OPTIONS } from "pages/Dashboard/OrderManagementDashboard/orderManagementDashboardUtils";

const DetailReport = props => {
  const { isOpenModal, dataDetail, toggleModal, mode, typeReportList } = props;
  const { t, i18n } = useTranslation(["reportManagement"]);
  const { addToast } = useToasts();

  const [isDetail, setIsDetail] = useState(false); // Prevent To Set Data Again After Success Detail

  const getInitDefaultValues = () => {
    return {
      statistical_type: undefined,
      time_report_date: [],
      report_year: undefined,
      order_status_ids: [],
      order_type_ids: [],
      kpi_status_ids: [],
      hub_id: [],
    };
  };

  const getInitControlFields = () => {
    return {
      display: {
        statistical_type: false,
        time_report_date: false,
        report_year: false,
        order_status_ids: false,
        order_type_ids: false,
        kpi_status_ids: false,
        hub_id: false,
        btn_save: false,
      },
      disabled: {
        statistical_type: false,
        time_report_date: false,
        report_year: false,
        order_status_ids: false,
        order_type_ids: false,
        kpi_status_ids: false,
        hub_id: false,
        btn_save: false,
      },
    };
  };

  const [controlFields, setControlFields] = useState(getInitControlFields);

  const [optionDateLocale, setOptionDateLocale] = useState({
    ...initOptionDateLocale,
  });

  const schema = yup.object().shape({
    statistical_type: yup
      .object()
      .required(
        t("form.validate.select", { title: t("form.statistical_type") })
      ),
    time_report_date: yup
      .array()
      .when("statistical_type", (statistical_type, yupNext) => {
        const typeCode = statistical_type?.code || "";
        switch (typeCode) {
          case "REPORT_TYPE_SYNTHESIS_ORDER":
          case "REPORT_TYPE_HISTORY_ORDER":
            return yupNext.min(
              2,
              t("form.validate.select", {
                title: t("form.time_report_date"),
              })
            );
          default:
            break;
        }
        return yupNext;
      }),
    order_status_ids: yup
      .array()
      .when("statistical_type", (statistical_type, yupNext) => {
        const typeCode = statistical_type?.code || "";
        switch (typeCode) {
          case "REPORT_TYPE_SYNTHESIS_ORDER":
          case "REPORT_TYPE_HISTORY_ORDER":
            return yupNext.min(
              1,
              t("form.validate.select", {
                title: t("form.order_status_ids"),
              })
            );
          default:
            break;
        }
        return yupNext;
      }),
  });

  const { data: hubList, refetch: refetchHubList } = useHubList({
    params: { page: -1 },
    run: false,
    key: "all-hub-report",
  });

  const reportYearOptions = buildReportYearOptions();

  const {
    handleSubmit,
    reset,
    formState: { errors },
    control,
    setValue,
    getValues,
  } = useForm({
    resolver: yupResolver(schema),
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: getInitDefaultValues(),
  });

  // List Order Status
  const { data: orderStatusList, refetch: fetchOrderStatus } = useOrderStatus();

  // Detail Report
  const onSuccessDetailReport = data => {
    reset(getInitDefaultValues());

    let objectType = null;
    if (typeReportList?.items && data.object_type) {
      objectType = typeReportList.items.find(
        item => String(item.id) === data.object_type
      );
      const setData = () => {
        setValue("statistical_type", {
          label: objectType.name,
          value: objectType.id,
          code: objectType.code,
        });

        if (data.ext_value?.from_date && data.ext_value?.to_date) {
          setValue("time_report_date", [
            new Date(data.ext_value.from_date),
            new Date(data.ext_value.to_date),
          ]);
        }

        if (data.ext_value?.status?.length > 0 && orderStatusList?.length > 0) {
          const orderStatusIds = [];
          data.ext_value.status.forEach(item => {
            const resultFind = orderStatusList.find(
              x => String(x.value) === String(item)
            );
            if (resultFind) {
              orderStatusIds.push(resultFind);
            }
          });
          setValue("order_status_ids", orderStatusIds);
        }

        if (objectType?.code === REPORT_TYPE_KPI_PERFORMANCE) {
          if (data.ext_value?.from_date) {
            const reportYear = String(data.ext_value.from_date).slice(0, 4);
            const yearOption = reportYearOptions.find(
              item => String(item.value) === reportYear
            );
            if (yearOption) {
              setValue("report_year", yearOption);
            }
          }

          if (data.ext_value?.order_type) {
            const selectedOrderTypes = String(data.ext_value.order_type)
              .split(",")
              .map(value => ORDER_TYPE_FILTER_OPTIONS.find(item => item.value === value.trim()))
              .filter(Boolean);
            setValue("order_type_ids", selectedOrderTypes);
          }

          if (data.ext_value?.kpi_status) {
            const selectedKpiStatuses = String(data.ext_value.kpi_status)
              .split(",")
              .map(value => KPI_STATUS_OPTIONS.find(item => item.value === value.trim()))
              .filter(Boolean);
            setValue("kpi_status_ids", selectedKpiStatuses);
          }
        }
      };
      if (objectType) {
        switch (objectType.code) {
          case "REPORT_TYPE_SYNTHESIS_ORDER":
            refetchHubList();
            setData();
            break;
          case REPORT_TYPE_KPI_PERFORMANCE:
            refetchHubList();
            setData();
            break;
          case "REPORT_TYPE_HISTORY_ORDER":
            setData();
            break;
          default:
            break;
        }
      }
    }

    switch (mode) {
      case reportModeList.VIEW_REPORT_DETAIL:
        if (objectType) {
          switch (objectType.code) {
            case "REPORT_TYPE_SYNTHESIS_ORDER":
              setControlFields({
                display: {
                  ...getInitControlFields().display,
                  statistical_type: true,
                  time_report_date: true,
                  order_status_ids: true,
                  hub_id: true,
                },
                disabled: {
                  ...getInitControlFields().disabled,
                  statistical_type: true,
                  time_report_date: true,
                  order_status_ids: true,
                  hub_id: true,
                },
              });
              break;
            case REPORT_TYPE_KPI_PERFORMANCE:
              setControlFields({
                display: {
                  ...getInitControlFields().display,
                  statistical_type: true,
                  report_year: true,
                  hub_id: true,
                  order_type_ids: true,
                  kpi_status_ids: true,
                },
                disabled: {
                  ...getInitControlFields().disabled,
                  statistical_type: true,
                  report_year: true,
                  hub_id: true,
                  order_type_ids: true,
                  kpi_status_ids: true,
                },
              });
              break;
            case "REPORT_TYPE_HISTORY_ORDER":
              setControlFields({
                display: {
                  ...getInitControlFields().display,
                  statistical_type: true,
                  time_report_date: true,
                  order_status_ids: true,
                },
                disabled: {
                  ...getInitControlFields().disabled,
                  statistical_type: true,
                  time_report_date: true,
                  order_status_ids: true,
                },
              });
              break;
            default:
              break;
          }
        }
        break;
      case reportModeList.EDIT_REPORT_DETAIL:
        break;
      default:
        break;
    }
  };
  const {
    isLoading: isLoadingDetail,
    data: reportDetail,
    refetch: fetchDetailReport,
    remove: resetDetailReport,
  } = useDetailReport(
    { reportId: dataDetail?.reportId },
    onSuccessDetailReport
  );

  // Language Change
  useEffect(() => {
    const lang = i18n.language;
    switch (lang) {
      case "vn":
        setOptionDateLocale({
          ...optionDateLocale,
          locale: {
            ...Vietnamese,
            ...initOptionDateLocale.locale,
          },
        });
        break;
      case "en":
        setOptionDateLocale({
          ...optionDateLocale,
          locale: {
            ...english,
            ...initOptionDateLocale.locale,
          },
        });
        break;
      default:
        break;
    }
  }, [t]);

  useEffect(() => {
    if (!isOpenModal) {
      resetDetailReport();
      setIsDetail(false);
      setControlFields(getInitControlFields());
      reset(getInitDefaultValues());
    }
  }, [isOpenModal]);

  // Action Detail
  useEffect(() => {
    if (isOpenModal) {
      if (dataDetail && dataDetail.reportId && !isDetail) {
        fetchDetailReport();
        setIsDetail(true);
      }
      fetchOrderStatus();
    }
  }, [dataDetail, isOpenModal]);

  useEffect(() => {
    if (hubList) {
      let options = [];
      hubList.items?.forEach(item => {
        if (reportDetail?.hub_id?.includes(`${item.id}`)) {
          options.push({
            label: item.name,
            value: item.id,
          });
        }
      });
      setValue("hub_id", options);
    }
  }, [hubList, reportDetail]);

  const onSubmit = _values => {};

  return (
    <React.Fragment>
      <Modal
        size="xl"
        isOpen={isOpenModal}
        role="dialog"
        autoFocus={true}
        centered={true}
        className="exampleModal"
        toggle={toggleModal}
      >
        <ModalHeader toggle={toggleModal}>
          {mode === reportModeList.VIEW_REPORT_DETAIL && `${t("title.detail")}`}
          {mode === reportModeList.EDIT_REPORT_DETAIL && `${t("title.edit")}`}
        </ModalHeader>
        {isLoadingDetail ? (
          <ModalBody>
            <LoadingFormNormal
              countInput="6"
              countButton="1"
              formClass="row-cols-1 row-cols-md-2"
              buttonClass="justify-content-end"
              buttonColClass="col-3"
            />
          </ModalBody>
        ) : (
          <React.Fragment>
            <ModalBody>
              <Row className="row-cols-1">
                {controlFields.display.statistical_type && (
                  <Col>
                    <FormGroup className="mb-3">
                      <Label htmlFor="statistical_type">
                        {t("form.statistical_type")}{" "}
                        <span className="text-danger">*</span>
                      </Label>
                      <Controller
                        control={control}
                        name="statistical_type"
                        render={({ field }) => (
                          <Select
                            {...field}
                            isDisabled={controlFields.disabled.statistical_type}
                            placeholder={t("placeholder.select", {
                              title: t("form.statistical_type"),
                            })}
                            options={
                              typeReportList?.items?.length > 0
                                ? typeReportList.items.map(item => {
                                    return {
                                      label: item.name,
                                      value: item.id,
                                      code: item.code,
                                    };
                                  })
                                : []
                            }
                            onChange={data => {
                              field.onChange(data);
                            }}
                            classNamePrefix="select2-selection"
                          />
                        )}
                      />
                      {errors.statistical_type && (
                        <MessageFormValidate
                          error={errors.statistical_type.message}
                        />
                      )}
                    </FormGroup>
                  </Col>
                )}
              </Row>
              <Row className="row-cols-1 row-cols-md-2">
                {controlFields.display.time_report_date && (
                  <Col>
                    <FormGroup className="mb-3">
                      <Label htmlFor="time_report_date">
                        {t("form.time_report_date")}{" "}
                        <span className="text-danger">*</span>
                      </Label>
                      <Controller
                        control={control}
                        name="time_report_date"
                        render={({ field }) => (
                          <Flatpickr
                            {...field}
                            disabled={controlFields.disabled.time_report_date}
                            className="form-control d-block Flatpickr__Input"
                            placeholder="DD/MM/YYYY - DD/MM/YYYY"
                            options={{
                              ...optionDateLocale,
                              mode: "range",
                            }}
                            onClose={dObj => {
                              // Check If Select Only One And Close => Set 2 Object Date
                              if (dObj && dObj.length === 1) {
                                setTimeout(() => {
                                  setValue(
                                    "time_report_date",
                                    [dObj[0], dObj[0]],
                                    { shouldValidate: true }
                                  );
                                }, 150);
                              }
                            }}
                          />
                        )}
                      />
                      {errors.time_report_date && (
                        <MessageFormValidate
                          error={errors.time_report_date.message}
                        />
                      )}
                    </FormGroup>
                  </Col>
                )}

                {controlFields.display.report_year && (
                  <Col>
                    <FormGroup className="mb-3">
                      <Label htmlFor="report_year">
                        {t("form.report_year")}{" "}
                        <span className="text-danger">*</span>
                      </Label>
                      <Controller
                        control={control}
                        name="report_year"
                        render={({ field }) => (
                          <Select
                            {...field}
                            isDisabled={controlFields.disabled.report_year}
                            options={reportYearOptions}
                            classNamePrefix="select2-selection"
                          />
                        )}
                      />
                    </FormGroup>
                  </Col>
                )}

                {controlFields.display.order_type_ids && (
                  <Col>
                    <FormGroup className="mb-3">
                      <Label htmlFor="order_type_ids">
                        {t("form.order_type_ids")}{" "}
                        <span className="text-danger">*</span>
                      </Label>
                      <Controller
                        control={control}
                        name="order_type_ids"
                        render={({ field }) => (
                          <Select
                            {...field}
                            isDisabled={controlFields.disabled.order_type_ids}
                            isMulti
                            options={ORDER_TYPE_FILTER_OPTIONS}
                            classNamePrefix="select2-selection"
                          />
                        )}
                      />
                    </FormGroup>
                  </Col>
                )}

                {controlFields.display.kpi_status_ids && (
                  <Col>
                    <FormGroup className="mb-3">
                      <Label htmlFor="kpi_status_ids">
                        {t("form.kpi_status_ids")}{" "}
                        <span className="text-danger">*</span>
                      </Label>
                      <Controller
                        control={control}
                        name="kpi_status_ids"
                        render={({ field }) => (
                          <Select
                            {...field}
                            isDisabled={controlFields.disabled.kpi_status_ids}
                            isMulti
                            options={KPI_STATUS_OPTIONS}
                            classNamePrefix="select2-selection"
                          />
                        )}
                      />
                    </FormGroup>
                  </Col>
                )}

                {controlFields.display.order_status_ids && (
                  <Col>
                    <FormGroup className="mb-3">
                      <Label htmlFor="order_status_ids">
                        {t("form.order_status_ids")}{" "}
                        <span className="text-danger">*</span>
                      </Label>
                      <Controller
                        control={control}
                        name="order_status_ids"
                        render={({ field }) => (
                          <Select
                            {...field}
                            isDisabled={controlFields.disabled.order_status_ids}
                            isMulti
                            placeholder={t("placeholder.select", {
                              title: t("form.order_status_ids"),
                            })}
                            options={
                              orderStatusList?.length > 0
                                ? orderStatusList.map(item => {
                                    return {
                                      value: item.value,
                                      label: item.label,
                                    };
                                  })
                                : []
                            }
                            classNamePrefix="select2-selection"
                            onChange={data => {
                              field.onChange(data);
                            }}
                          />
                        )}
                      />
                      {errors.order_status_ids && (
                        <MessageFormValidate
                          error={errors.order_status_ids.message}
                        />
                      )}
                    </FormGroup>
                  </Col>
                )}
                {controlFields.display.hub_id && (
                  <Col>
                    <FormGroup className="mb-3">
                      <Label htmlFor="hub_id">
                        {t("form.hub_id")}{" "}
                        <span className="text-danger">*</span>
                      </Label>
                      <Controller
                        control={control}
                        name="hub_id"
                        render={({ field }) => (
                          <Select
                            {...field}
                            isDisabled={controlFields.disabled.hub_id}
                            isMulti
                            placeholder={t("placeholder.select", {
                              title: t("form.hub_id"),
                            })}
                            classNamePrefix="select2-selection"
                          />
                        )}
                      />
                      {errors.hub_id && (
                        <MessageFormValidate error={errors.hub_id.message} />
                      )}
                    </FormGroup>
                  </Col>
                )}
              </Row>
            </ModalBody>
            <ModalFooter>
              {/* <Row className="justify-content-end">
                {controlFields.display.btn_save && (
                  <Col className="col-auto">
                    <Button
                      color="primary"
                      className="btn-label"
                      type="button"
                      onClick={() => {
                        handleSubmit(onSubmit)();
                      }}
                      disabled={
                        isLoadingDetail || controlFields.disabled.btn_save
                      }
                    >
                      <i className="far fa-save label-icon"></i>
                      {t("form.export_report")}
                    </Button>
                  </Col>
                )}
              </Row> */}
            </ModalFooter>
          </React.Fragment>
        )}
      </Modal>
    </React.Fragment>
  );
};

DetailReport.propTypes = {
  isOpenModal: PropTypes.bool.isRequired,
  toggleModal: PropTypes.func.isRequired,
  dataDetail: PropTypes.object.isRequired,
  mode: PropTypes.string.isRequired,
  typeReportList: PropTypes.any,
};

DetailReport.defaultProps = {
  isOpenModal: false,
  dataDetail: {},
  mode: "",
};

export default DetailReport;
